import React from "react";
import {
  BookOpen,
  FileText,
  Headphones,
  ListVideo,
  type LucideIcon,
  Newspaper,
  Video,
} from "lucide-react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useAppDispatch } from "@/app/hooks";
import { setScrollPosition } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import { trackResourceCardClick } from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";
import { trackPodcastCardClick } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsCardClick } from "@/modules/hub/features/News/analytics/newsAnalytics";
import { getResourceTradeStyling } from "@/modules/hub/features/ResourceLibrary/utils/tradeStyling";

// Maps a resource type to a lucide icon component (no raw HTML injection).
export function getResourceTypeIcon(resourceType: string | null): LucideIcon {
  switch ((resourceType || "").toLowerCase()) {
    case "video":
      return Video;
    case "series":
      return ListVideo;
    case "article":
    case "news":
      return Newspaper;
    case "podcast":
      return Headphones;
    case "guide":
      return BookOpen;
    case "file":
    default:
      return FileText;
  }
}

// Resource Library v2: Article resources use the new thumbnail2 image; all other
// types keep the standard thumbnail. Falls back to the standard thumbnail for
// Articles that don't have a v2 image yet. Shared by the resource card and the
// featured carousel, so V2 renders article thumbnails consistently.
export function getResourceThumbnailUrl(
  resource: GetResourceSearchResultsItem,
): string | null {
  if (resource.resourceType === "Article") {
    return resource.thumbnail2Url ?? resource.thumbnailUrl;
  }

  return resource.thumbnailUrl;
}

// Maps a resource type to a muted, natural gradient for the placeholder
// thumbnail (used when a resource has no uploaded image). Full class strings so
// Tailwind can detect them.
function getResourceTypeGradient(resourceType: string | null): string {
  switch ((resourceType || "").toLowerCase()) {
    case "video":
      return "from-orange-300 to-orange-400 dark:from-orange-600 dark:to-orange-800";
    case "file":
      return "from-rose-300 to-rose-400 dark:from-rose-600 dark:to-rose-800";
    case "article":
      return "from-stone-300 to-stone-400 dark:from-stone-600 dark:to-stone-800";
    case "news":
      return "from-teal-300 to-teal-400 dark:from-teal-600 dark:to-teal-800";
    case "series":
      return "from-violet-300 to-violet-400 dark:from-violet-600 dark:to-violet-800";
    case "podcast":
      return "from-amber-300 to-amber-400 dark:from-amber-600 dark:to-amber-800";
    case "guide":
      return "from-indigo-300 to-indigo-400 dark:from-indigo-600 dark:to-indigo-800";
    default:
      return "from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-800";
  }
}

// The card header line: the process wheel followed by the first business role,
// e.g. ["ONBOARDING", "NEW MEMBER"]. Shared by the resource card and the
// featured carousel, so both render the same taxonomy.
export function getResourceTaxonomyParts(
  resource: GetResourceSearchResultsItem,
): string[] {
  const processWheel = resource.processWheel ?? undefined;
  const businessRole = resource.roles?.[0]?.name;
  return [processWheel, businessRole].filter((part): part is string =>
    Boolean(part),
  );
}

// Encapsulates the data derivation, trade styling, and event handlers for a
// resource card so the component stays purely presentational.
export function useResourceCardV2(
  resource: GetResourceSearchResultsItem,
  onToggleFavorite?: (uuid: string) => void | Promise<void>,
) {
  const dispatch = useAppDispatch();

  const isFavorited = resource.isFavorited ?? false;
  const businessRole = resource.roles?.[0]?.name;
  const taxonomyLabel = getResourceTaxonomyParts(resource).join(" | ");
  const description = resource.searchDescription;

  // Trade badge descriptor + its badge/accent color classes. tradeColorClass is
  // null when the resource has no trade, so each element keeps its own default.
  const { tradeBadge, tradeBadgeClass, tradeColorClass } =
    getResourceTradeStyling(resource);

  const TypeIcon = getResourceTypeIcon(resource.resourceType);
  const typeGradient = getResourceTypeGradient(resource.resourceType);
  const typeLabel = resource.resourceType || "Document";

  const thumbnailUrl = getResourceThumbnailUrl(resource);

  const isPartnerPage =
    resource.resourceType === "Partner Page" && Boolean(resource.contentUrl);
  const href = isPartnerPage
    ? resource.contentUrl || ""
    : `/hub/resources/${resource.slug}`;

  const handleCardClick = () => {
    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    dispatch(setScrollPosition(scrollY));

    const state = {
      scrollY,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(
      "resourceLibrary_scrollState",
      JSON.stringify(state),
    );

    if (resource.resourceType === "Podcast") {
      trackPodcastCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else if (resource.resourceType === "News") {
      trackNewsCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else {
      trackResourceCardClick({
        resourceType: resource.resourceType || "Unknown",
        resourceUuid: resource.uuid,
      });
    }
  };

  const handleToggleFavorite = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    await toggleResourceFavorite({ resourceUuid: resource.uuid });

    if (onToggleFavorite) {
      await onToggleFavorite(resource.uuid);
    }
  };

  return {
    isFavorited,
    businessRole,
    taxonomyLabel,
    description,
    tradeBadge,
    tradeBadgeClass,
    tradeColorClass,
    TypeIcon,
    typeGradient,
    typeLabel,
    isPartnerPage,
    href,
    thumbnailUrl,
    handleCardClick,
    handleToggleFavorite,
  };
}
